/**
 * Ernaehrungsanalyse (F-35 D) - rein und getestet. Der Score 1-10 setzt sich
 * aus Kriterien zusammen, die Ole selbst auswaehlt und gewichtet; jede
 * Bewertung ist aufgeschluesselt. Alles rechnet nur mit den eigenen
 * Eintraegen - kein Lebensmittel wird pauschal als gut oder schlecht eingestuft.
 */
import type { ErnaehrungsKriterium, LocalDate } from '@/domain/types';
import { erreicht, type TagZeile } from '@/domain/ernaehrung/ueberblick';
import { skalieren, type Daten } from '@/domain/ernaehrung/naehrwerte';
import type { Getraenk, LebensmittelGruppe, Mahlzeit, MakroKey, Mikro, Naehrwerte } from '@/domain/ernaehrung/typen';

export type KriteriumArt = ErnaehrungsKriterium;

export interface Kriterium { art: KriteriumArt; aktiv: boolean; gewicht: number; wert?: number }

export const KRITERIEN: Record<KriteriumArt, { label: string; kurz: string; einheit?: string; mitWert: boolean }> = {
  eiweissZiel: { label: 'Eiweißziel erreicht', kurz: 'Eiweiß', mitWert: false },
  trinkZiel: { label: 'Trinkziel erreicht', kurz: 'Wasser', mitWert: false },
  kcalZiel: { label: 'Kalorien im Zielbereich', kurz: 'Kalorien', mitWert: false },
  ballastMin: { label: 'Ballaststoffe mindestens', kurz: 'Ballaststoffe', einheit: 'g', mitWert: true },
  zuckerMax: { label: 'Zucker höchstens', kurz: 'Zucker', einheit: 'g', mitWert: true },
  salzMax: { label: 'Salz höchstens', kurz: 'Salz', einheit: 'g', mitWert: true },
  gemueseObstMin: { label: 'Gemüse und Obst mindestens', kurz: 'Gemüse/Obst', einheit: 'Portionen', mitWert: true },
  suessFastfoodMax: { label: 'Süßes und Fast Food höchstens', kurz: 'Süßes/Fast Food', einheit: 'Portionen', mitWert: true },
};

/**
 * Ausgangsauswahl - die Zahlen sind die DGE-Richtwerte (siehe referenz.ts);
 * „Süßes und Fast Food“ hat keinen Richtwert und ist deshalb aus, bis Ole
 * selbst eine Grenze setzt.
 */
export const STANDARD_KRITERIEN: Kriterium[] = [
  { art: 'eiweissZiel', aktiv: true, gewicht: 1 },
  { art: 'trinkZiel', aktiv: true, gewicht: 1 },
  { art: 'kcalZiel', aktiv: true, gewicht: 1 },
  { art: 'ballastMin', aktiv: true, gewicht: 1, wert: 30 },
  { art: 'zuckerMax', aktiv: true, gewicht: 1, wert: 50 },
  { art: 'salzMax', aktiv: true, gewicht: 1, wert: 6 },
  { art: 'gemueseObstMin', aktiv: true, gewicht: 1, wert: 5 },
  { art: 'suessFastfoodMax', aktiv: false, gewicht: 1 },
];

export type Gruppen = Partial<Record<LebensmittelGruppe, number>>;

export interface Pruefung {
  art: KriteriumArt;
  gewicht: number;
  erfuellt: boolean;
  /** Kurzname des Kriteriums, z. B. "Wasser" - erfuellt oder nicht zeigt die Oberflaeche als Symbol (F-39). */
  text: string;
  /** Nur wenn verfehlt: wie weit daneben, z. B. "−1.100 ml". */
  abweichung?: string;
}

const z = (v: number, s = 0) => v.toLocaleString('de-DE', { maximumFractionDigits: s });
const diff = (v: number, einheit: string, s = 0) => `${v > 0 ? '+' : '−'}${z(Math.abs(v), s)}${einheit ? ` ${einheit}` : ''}`;

/** Ein Kriterium an einem Tag - `null`, wenn ohne Ziel bzw. Grenze oder ohne Daten nicht pruefbar. */
export function pruefe(k: Kriterium, t: TagZeile, g: Gruppen, spanne?: number): Pruefung | null {
  const w = t.summe.werte;
  const kurz = KRITERIEN[k.art].kurz;
  const ergebnis = (erfuellt: boolean, abweichung?: string): Pruefung => ({ art: k.art, gewicht: k.gewicht, erfuellt, text: kurz, ...(!erfuellt && abweichung ? { abweichung } : {}) });
  const ziel = (key: 'eiweiss' | 'kcal' | 'wasserMl', wert: number | undefined, einheit: string) => {
    const r = erreicht(key, wert, t.ziele[key], spanne);
    return r === undefined ? null : ergebnis(r, diff((wert ?? 0) - t.ziele[key]!, einheit));
  };
  const grenze = (wert: number | undefined, mind: boolean, einheit: string, s = 0) => {
    if (k.wert === undefined || wert === undefined) return null;
    return ergebnis(mind ? wert >= k.wert : wert <= k.wert, diff(wert - k.wert, einheit, s));
  };
  switch (k.art) {
    case 'eiweissZiel': return ziel('eiweiss', w.eiweiss, 'g');
    case 'kcalZiel': return ziel('kcal', w.kcal, 'kcal');
    case 'trinkZiel': return ziel('wasserMl', t.wasserMl, 'ml');
    case 'ballastMin': return grenze(w.ballast, true, 'g');
    case 'zuckerMax': return grenze(w.zucker, false, 'g');
    case 'salzMax': return grenze(w.salz, false, 'g', 1);
    case 'gemueseObstMin': return grenze((g.gemuese ?? 0) + (g.obst ?? 0), true, 'Portionen', 1);
    case 'suessFastfoodMax': return grenze((g.suess ?? 0) + (g.fastfood ?? 0), false, 'Portionen', 1);
  }
}

export interface Score { score: number | null; teile: Pruefung[]; nichtPruefbar: KriteriumArt[] }

/** Score = 1 + 9 × gewichteter Anteil erfuellter Kriterien, gerundet. Nur fuer vollstaendige Tage. */
export function tagesScore(t: TagZeile, g: Gruppen, kriterien: Kriterium[], spanne?: number): Score {
  const aktiv = kriterien.filter((k) => k.aktiv && k.gewicht > 0);
  const teile: Pruefung[] = [];
  const nichtPruefbar: KriteriumArt[] = [];
  for (const k of aktiv) { const p = pruefe(k, t, g, spanne); if (p) teile.push(p); else nichtPruefbar.push(k.art); }
  if (!t.vollstaendig || !teile.length) return { score: null, teile, nichtPruefbar };
  const summe = teile.reduce((s, p) => s + p.gewicht, 0);
  const ja = teile.reduce((s, p) => s + (p.erfuellt ? p.gewicht : 0), 0);
  return { score: Math.round(1 + 9 * (ja / summe)), teile, nichtPruefbar };
}

/** Eine Pruefung als reiner Text, z. B. fuer Tooltips: "Wasser verfehlt (−1.100 ml)". */
export const pruefText = (p: Pruefung) => `${p.text} ${p.erfuellt ? 'erfüllt' : 'verfehlt'}${p.abweichung ? ` (${p.abweichung})` : ''}`;

export const scoreText = (s: Score) => (s.score === null ? 'kein Score' : `${s.score}/10: ${s.teile.map(pruefText).join(', ')}`);

const mittel = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

export interface TagMitScore { zeile: TagZeile; gruppen: Gruppen; score: Score }

/** Gut und Verbesserungspotenzial ueber einen Zeitraum (nur vollstaendige Tage). */
export function bilanz(tage: TagMitScore[]): { gut: string[]; besser: string[]; schnitt: number | null } {
  const voll = tage.filter((t) => t.zeile.vollstaendig);
  const gut: string[] = [];
  const besser: string[] = [];
  const arten = [...new Set(voll.flatMap((t) => t.score.teile.map((p) => p.art)))];
  for (const art of arten) {
    const mit = voll.filter((t) => t.score.teile.some((p) => p.art === art));
    const ja = mit.filter((t) => t.score.teile.find((p) => p.art === art)!.erfuellt).length;
    const kurz = KRITERIEN[art].kurz;
    // Laengste Serie am Stueck (aufeinanderfolgende Tage im Zeitraum).
    let serie = 0, lauf = 0, vorher: LocalDate | null = null;
    for (const t of voll) {
      const ok = t.score.teile.find((p) => p.art === art)?.erfuellt === true;
      const anschluss = vorher !== null && naechsterTag(vorher) === t.zeile.datum;
      lauf = ok ? (anschluss ? lauf + 1 : 1) : 0;
      serie = Math.max(serie, lauf);
      vorher = t.zeile.datum;
    }
    if (ja === mit.length && mit.length >= 2) gut.push(`${kurz}: an allen ${mit.length} Tagen erreicht`);
    else if (serie >= 3) gut.push(`${kurz}: ${serie} Tage in Folge erreicht`);
    else if (ja > mit.length / 2) gut.push(`${kurz}: an ${ja} von ${mit.length} Tagen erreicht`);
    if (mit.length - ja > 0 && ja <= mit.length / 2) besser.push(`${kurz}: an ${mit.length - ja} von ${mit.length} Tagen verfehlt`);
  }
  const beste = voll.filter((t) => t.score.score !== null).sort((a, b) => b.score.score! - a.score.score!)[0];
  if (beste && beste.score.score! >= 8) gut.unshift(`Bester Tag: ${beste.zeile.datum.slice(8, 10)}.${beste.zeile.datum.slice(5, 7)}. mit ${beste.score.score}/10`);
  return { gut, besser, schnitt: mittel(voll.flatMap((t) => (t.score.score !== null ? [t.score.score] : []))) };
}

const naechsterTag = (d: LocalDate): LocalDate => {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + 1);
  return x.toISOString().slice(0, 10) as LocalDate;
};

// --- Groesste Quellen ----------------------------------------------------------

export type QuellenKey = MakroKey | Mikro;
export interface Quelle { name: string; wert: number; anteil: number }

/**
 * Welche Lebensmittel bzw. Getraenke tragen am meisten zu einem Wert bei?
 * Rezepte werden in ihre Zutaten aufgeloest, Schnelleintraege zaehlen unter
 * ihrem Namen. Ergebnis: die groessten mit Anteil in Prozent.
 */
export function topQuellen(tage: { mahlzeiten: Mahlzeit[]; getraenke: Getraenk[] }[], d: Daten, key: QuellenKey, anzahl = 5): { quellen: Quelle[]; gesamt: number } {
  const je = new Map<string, number>();
  const plus = (name: string, w: Naehrwerte) => {
    const v = key in w ? (w[key as MakroKey] as number | undefined) : w.mikro?.[key as Mikro];
    if (v) je.set(name, (je.get(name) ?? 0) + v);
  };
  for (const t of tage) {
    for (const p of t.mahlzeiten.flatMap((m) => m.posten)) {
      if (p.art === 'schnell') plus(p.name, p.werte);
      else if (p.art === 'lebensmittel') { const l = d.lebensmittel.get(p.refId); if (l) plus(l.name, skalieren(l.je100, p.menge / 100)); }
      else {
        const r = d.rezepte.get(p.refId);
        if (!r) continue;
        for (const zt of r.zutaten) { const l = d.lebensmittel.get(zt.lebensmittelId); if (l) plus(l.name, skalieren(l.je100, (zt.menge / Math.max(1, r.portionen)) * p.menge / 100)); }
      }
    }
    for (const g of t.getraenke) { const l = g.lebensmittelId ? d.lebensmittel.get(g.lebensmittelId) : undefined; if (l) plus(l.name, skalieren(l.je100, g.ml / 100)); }
  }
  const gesamt = [...je.values()].reduce((s, v) => s + v, 0);
  const quellen = [...je.entries()].sort((a, b) => b[1] - a[1]).slice(0, anzahl).map(([name, wert]) => ({ name, wert, anteil: gesamt ? Math.round((wert / gesamt) * 100) : 0 }));
  return { quellen, gesamt };
}

// --- Zusammenhaenge mit dem Journal --------------------------------------------

export interface Befinden { energie?: number; stimmung?: number; schlaf?: number }
export const MIN_TAGE_ZUSAMMENHANG = 3;

/**
 * Je Kriterium: Ø Befinden an Tagen, an denen es erfuellt war, und an den
 * anderen. Erst ab je 3 Tagen. Beschreibend - keine Ursache.
 */
export function zusammenhaenge(tage: TagMitScore[], befinden: (d: LocalDate) => Befinden | undefined) {
  const out: { art: KriteriumArt; mit: { tage: number; energie: number | null; stimmung: number | null; schlaf: number | null }; ohne: { tage: number; energie: number | null; stimmung: number | null; schlaf: number | null } }[] = [];
  const voll = tage.filter((t) => t.zeile.vollstaendig);
  const arten = [...new Set(voll.flatMap((t) => t.score.teile.map((p) => p.art)))];
  const gruppe = (ts: TagMitScore[]) => {
    const b = ts.map((t) => befinden(t.zeile.datum)).filter((x): x is Befinden => Boolean(x));
    const m = (f: (x: Befinden) => number | undefined) => { const v = mittel(b.flatMap((x) => { const y = f(x); return y === undefined ? [] : [y]; })); return v === null ? null : Math.round(v * 10) / 10; };
    return { tage: b.length, energie: m((x) => x.energie), stimmung: m((x) => x.stimmung), schlaf: m((x) => x.schlaf) };
  };
  for (const art of arten) {
    const mit = voll.filter((t) => t.score.teile.find((p) => p.art === art)?.erfuellt === true);
    const ohne = voll.filter((t) => t.score.teile.find((p) => p.art === art)?.erfuellt === false);
    const a = gruppe(mit), b = gruppe(ohne);
    if (a.tage >= MIN_TAGE_ZUSAMMENHANG && b.tage >= MIN_TAGE_ZUSAMMENHANG) out.push({ art, mit: a, ohne: b });
  }
  return out;
}
